import { DateTime } from "luxon";

const {
    supabaseFetchRoutes,
    supabaseFetchProtocols,
    updateProtocolInSupabase,
} = useSupabase();
// ToDo: Setup store for routeplanning
export const useRouteStore = defineStore("routes", {
    state: () => ({
        routePlanningData: null as routeData[] | null, // first load at routeplanning/tomorrow, can be changed
        routeDataForDate: null as routeData | null, // route data for the date being viewed
        routeExecution: {
            currentRouteIndex: null, // current route in [route].vue
            currentStopIndex: 0, // current route progress // ToDo: next to the currentstopindex (where driver is at), we also need the currentlyViewedIndex
            currentStep: 0, // current step for current stop viewed in [route].vue
            currentDelay: 0, // can be positive for delay, negative for being earlier than planned
            delayReason: null,
            overviewMode: true,
        },
        currentDate: null, // the date currently being viewed
        protocols: [] as protocol[], // Speichert Protokolle für verschiedene Routen
        addressChanges: [],
        timeChanges: [],
        dateChanges: [],
    }),
    getters: {
        currentStopIndex: (state) => state.routeExecution.currentStopIndex,
        isFirstStop: (state) => state.routeExecution.currentStopIndex === 0,
        isLastStop: (state) =>
            state.routeExecution.currentStopIndex ===
            state.routeDataForDate?.routes[
                state.routeExecution.currentRouteIndex
            ]?.route.length -
                1,
        currentStep: (state) => state.routeExecution.currentStep,
        currentProtocol: (state) =>
            state.protocols?.find(
                (p) => p?.routeId === state.routeExecution.currentRouteIndex,
            )?.entries || [],
        //currentRouteFinished: (state) => state.protocols?.find((p) => p?.routeId === state.routeExecution.currentRouteIndex)?.finished || false,
    },
    actions: {
        async initRouteplanning(date) {
            this.routePlanningData = await supabaseFetchRoutes(date);
        },
        async initRouteExecution(date) {
            const store = useDefaultStore();
            store.processing = true;
            try {
                console.log(
                    `[RouteStore] initRouteExecution called with date: ${date}`,
                );
                this.checkDate(date);

                console.log(
                    `[RouteStore] Fetching fresh route data for date: ${date}`,
                );
                const allDataForDate = await supabaseFetchRoutes(date);
                console.log(`[RouteStore] Fetched route data:`, allDataForDate);

                if (allDataForDate && allDataForDate.length > 0) {
                    // only the latest version is needed
                    // latest version is the one with the highest index
                    const index = allDataForDate.length - 1;
                    this.routeDataForDate = allDataForDate[index];
                    console.log(
                        `[RouteStore] Using route data at index ${index}:`,
                        this.routeDataForDate,
                    );
                } else {
                    this.routeDataForDate = null;
                    console.log(
                        `[RouteStore] No route data found for date: ${date}`,
                    );
                }

                console.log(
                    `[RouteStore] Fetching protocols for date: ${date}`,
                );
                this.protocols = await supabaseFetchProtocols(date);
                console.log(`[RouteStore] Protocols:`, this.protocols);
            } finally {
                store.processing = false;
            }
        },
        checkDate(targetDate: string) {
            if (this.currentDate !== targetDate) {
                // resetting all saved states when switching to a different date
                this.currentDate = targetDate;
                this.routeDataForDate = null;
                this.routeExecution = {
                    currentRouteIndex: null,
                    currentStopIndex: 0,
                    currentStep: 1,
                    currentDelay: 0,
                    delayReason: null,
                    overviewMode: true,
                };
                this.protocols = [];
                this.addressChanges = [];
                this.timeChanges = [];
                this.dateChanges = [];
            }
        },
        changeCurrentRoute(newRouteIndex) {
            console.log("New route id: " + newRouteIndex);
            console.log(
                "current route id: " + this.routeExecution.currentRouteIndex,
            );

            if (this.routeExecution.currentRouteIndex !== newRouteIndex) {
                this.routeExecution.currentRouteIndex = newRouteIndex;
                this.routeExecution.currentStopIndex =
                    this.getCurrentIndex(newRouteIndex);
                console.log(
                    "Current stop index: " +
                        this.routeExecution.currentStopIndex,
                );
                this.routeExecution.currentStep = this.getStepOfCurrentStop();
                this.routeExecution.currentDelay =
                    this.getDelayFromProtocol(newRouteIndex);
                this.routeExecution.overviewMode = true;
                console.log(this.routeExecution.currentDelay);
            } // new route was clicked, otherwise we pick up where we left off
            //else this.routeExecution.currentStopIndex = Math.max(0, this.routeExecution.currentStopIndex); // making sure index is always > 0
        },
        isRouteStarted(routeIndex) {
            const protocol = this.protocols?.find(
                (p) => p?.routeId === routeIndex,
            );
            if (protocol) return true;
            return false;
        },
        isRouteFinished(routeIndex?) {
            if (routeIndex === undefined)
                routeIndex = this.routeExecution.currentRouteIndex;
            // get protocol for this route and check if finished is true
            const protocol = this.protocols?.find(
                (p) => p?.routeId === routeIndex,
            );

            return protocol ? protocol.finished : false;
        },
        getDelayFromProtocol(routeIndex) {
            const protocol = this.protocols?.find(
                (p) => p?.routeId === routeIndex,
            );
            if (protocol) {
                // get the delay from the last entry in the protocol
                if (
                    protocol.entries[protocol.entries.length - 1]
                        .actualDeliveryTime
                ) {
                    return protocol.entries[protocol.entries.length - 1]?.delay;
                } else {
                    // if last entry hasnt been delivered yet there is no delay saved yet, so we take the delay from the item before that
                    return protocol.entries[protocol.entries.length - 2]?.delay;
                }
            }
            // if there is no protocol the route hasnt been started yet so there cant be a delay
            return 0;
        },
        getCurrentIndex(routeIndex) {
            // check if there is a protocol for this route
            const protocol = this.protocols.find(
                (p) => p.routeId === routeIndex,
            );
            console.log("Protocol: " + JSON.stringify(protocol));
            if (protocol) {
                // amount of entries in protocol is the amount of stops that are finished/started
                const lastEntry = protocol.entries[protocol.entries.length - 1];
                console.log("Last entry: " + JSON.stringify(lastEntry));
                if (lastEntry.actualDeliveryTime) {
                    console.log("last entry has a delivery time");
                    return protocol.entries.length;
                } else {
                    return protocol.entries.length - 1;
                }
            }
            return 0;
        },
        nextStop() {
            // more like finishstop, since this brings you back to the overview
            console.log(
                "Next stop, index is: " + this.routeExecution.currentStopIndex,
            );
            this.routeExecution.currentStopIndex++;
            // the navigation is now handled by the overview component
            this.routeExecution.overviewMode = true;
            this.routeExecution.currentStep = this.getStepOfCurrentStop();
        },
        moveStop(index) {
            // if "vorziehen" button is clicked
            // only if route isnt finished we need extra prompts, otherwise every stop can be viewed again
            const answer = window.confirm(
                "Soll dieser Stopp wirklich vorgezogen werden?",
            );
            if (!answer) {
                console.log("User did not confirm. Stay at overview.");
                return;
            } else {
                console.log("User confirmed. Change route.");
                const currentIndex = this.routeExecution.currentStopIndex;
                // change route object in routeDataToday by moving this stop to current index and shifting the rest of the route
                const routeId = this.routeExecution.currentRouteIndex;
                const route = this.routeDataForDate.routes[routeId];

                // remove stop to move from route (but save stop to add it later)
                const element = route.route.splice(index, 1)[0];
                // add stop at currentIndex, all remaining stops will be
                route.route.splice(currentIndex, 0, element);

                // add info "prioritized" to route data, to show when stop was moved
                route.route[currentIndex].prioritized = true;

                // update arrival and departure times of this and all remaining stops by accessing traveltime in distance matrix
                for (let i = currentIndex; i < route.route.length; i++) {
                    // get traveltime from stop i to i+1 through stop id
                    const previousStop = route.route[i - 1];
                    const thisStop = route.route[i];
                    const from = route.distance_matrix.find(
                        (f) => f.originId === previousStop.id,
                    );
                    const to = from.destinations.find(
                        (t) => t.destinationId === thisStop.id,
                    );
                    const travelTime = to.travelTimeMinutes;
                    route.route[i].arrivalTime = DateTime.fromISO(
                        route.route[i - 1].departureTime,
                    ).plus({ minutes: previousStop.waitingTime + travelTime });

                    // find setup time in deliveries
                    const delivery = this.routeDataForDate.deliveries.find(
                        (d) => d.id === thisStop.id,
                    );

                    if (delivery) {
                        const setupTime = delivery.setupTime;
                        route.route[i].departureTime = DateTime.fromISO(
                            route.route[i].arrivalTime,
                        ).plus({ minutes: setupTime });
                    }
                }

                console.log(
                    "Route after changing order of stops: " +
                        JSON.stringify(route.route),
                );
                // updating route in routeDataForDate
                this.routeDataForDate.routes[routeId] = route;
                // dont go to stop yet, stay in overview
                //this.goToStop(currentIndex);
            }
        },
        goToStop(index) {
            console.log("Going to stop: " + index);
            this.routeExecution.currentStopIndex = index;
            this.routeExecution.currentStep = this.getStepOfCurrentStop();
            this.routeExecution.overviewMode = false;
        },
        previousStop() {
            this.routeExecution.currentStopIndex--;
            if (this.isFirstStop) {
                this.routeExecution.currentStep = 0;
            } else {
                this.routeExecution.currentStep = this.getStepOfCurrentStop();
            }
        },
        changeStep(newStep) {
            this.routeExecution.currentStep = newStep;
            console.log("new step: " + this.currentStep);
        },
        getProtocolEntry(stopId: string, routeId?) {
            if (routeId === undefined)
                routeId = this.routeExecution.currentRouteIndex;
            const protocol = this.protocols?.find(
                (p) => p?.routeId === routeId,
            );
            if (protocol) {
                const stopEntry = protocol.entries.find(
                    (e) => e.stop_id === stopId,
                );
                return stopEntry;
            }
            return null;
        },
        updateProtocol(entry: protocolEntry) {
            const routeId = this.routeExecution.currentRouteIndex;
            // saving prioritiation in protocol
            entry.prioritized = this.routeDataForDate.routes[
                routeId
            ].route.find((s) => s.id == entry.stop_id)?.prioritized;
            let protocol = this.protocols?.find((p) => p?.routeId === routeId);
            if (!protocol) {
                this.protocols.push({
                    routeId: routeId,
                    entries: [],
                    finished: false,
                });
                protocol = this.protocols.find((p) => p.routeId === routeId);
            }

            // if stop_id is "kitchenEnd", set finished to true
            if (entry.stop_id === "kitchenEnd") {
                protocol.finished = true;
            }

            if (protocol.entries.length === 0) {
                protocol.entries.push(entry);
            } else {
                const existingEntry = protocol.entries.find(
                    (p) => p.stop_id === entry.stop_id,
                );

                if (existingEntry) {
                    // Wenn ein Eintrag existiert, aktualisiere ihn
                    console.log("Protokoll für diesen Stop wird aktualisiert.");
                    Object.assign(existingEntry, entry);
                } else {
                    // Wenn kein Eintrag existiert, erstelle einen neuen
                    console.log("Protokoll für diesen Stop wird erstellt.");
                    protocol.entries.push(entry);
                }
            }
        },
        // if no routeId is provided, routeId is current route index
        isFinished(index, routeId?) {
            if (routeId === undefined) {
                routeId = this.routeExecution.currentRouteIndex;
            }
            const protocol = this.protocols?.find(
                (p) => p?.routeId === routeId,
            );
            const stopId =
                this.routeDataForDate.routes[routeId]?.route[index]?.id;

            if (protocol?.entries) {
                // if last stop, id is "kitchenEnd" in protocol
                let stop;
                if (
                    index ===
                    this.routeDataForDate?.routes[routeId]?.route.length - 1
                ) {
                    stop = protocol.entries.find(
                        (e) => e.stop_id === "kitchenEnd",
                    );
                    if (stop?.actualArrivalTime) {
                        //console.log("Route finished- back in kitchen.");
                        return true;
                    }
                } else {
                    stop = protocol.entries.find((e) => e.stop_id === stopId);
                    if (stop?.actualDeliveryTime) {
                        //console.log("Delivery is done for this stop.");
                        return true;
                    }
                }
            }
            return false;
        },
        async finishRoute() {
            // Add current time as arrival time for this stop in protocol
            console.log("Finishing Route!!");
            this.updateProtocol({
                stop_id: "kitchenEnd",
                actualArrivalTime: DateTime.now(),
            });
            const routeId = this.routeExecution.currentRouteIndex;

            const protocol = this.protocols?.find(
                (p) => p?.routeId === routeId,
            );
            protocol.finished = true;

            console.log("is route finished? " + protocol.finished);

            const fsStore = useFunctionsheetStore();

            updateProtocolInSupabase(
                this.currentDate,
                routeId,
                this.currentProtocol,
                true,
            );
            this.routeExecution.currentRouteIndex =
                this.routeDataForDate.routes[routeId].route.length - 1;
            this.routeExecution.overviewMode = true;

            // updating protocols to have the latest update
            this.protocols = await supabaseFetchProtocols(this.currentDate);

            //protocol.date = DateTime.now().toISODate();
            //routeStore.finishedRoutes.push(routeId);
        },
        getStepOfCurrentStop() {
            // get at which step the currently viewed stop is at
            // depending on which data is stored in the protocol
            if (this.isLastStop || this.isFirstStop) {
                return 0;
            }
            console.log("Getting step of current stop");
            const routeId = this.routeExecution.currentRouteIndex;
            console.log("Route id: " + routeId);
            const protocol = this.protocols?.find(
                (p) => p?.routeId === routeId,
            );
            console.log("Protocol: " + JSON.stringify(protocol));
            if (!protocol || protocol.entries.length === 0) {
                console.log(
                    "No protocol found for this route or no entries in protocol.",
                );
                return 1;
            }

            const stop =
                this.routeDataForDate?.routes?.[routeId]?.route?.[
                    this.routeExecution.currentStopIndex
                ];

            if (!stop) {
                console.warn(
                    "getStepOfCurrentStop: Could not find stop data. This can happen on initial load.",
                );
                return 1;
            }
            const stopId = stop.id;

            console.log("Stop id: " + stopId);
            const existingEntry = protocol.entries.find(
                (p) => p.stop_id === stopId,
            );

            if (existingEntry) {
                console.log("Existing entry found");
                // if actualArrivalTime is set, but actualDelivery is not set, the step is 2
                // if delay is set, the step is 3?
                // if actualdeliverytime is setactualDepartureTime is NOT set, the step is 4

                if (
                    existingEntry.actualArrivalTime &&
                    !existingEntry.actualDeliveryTime
                ) {
                    console.log("Arrival is done, step: delivery");
                    return 2;
                } else if (
                    existingEntry.actualDeliveryTime &&
                    !existingEntry.actualDepartureTime
                ) {
                    console.log("Delivery is done, step: leaving stop.");
                    return 4;
                } else if (existingEntry.actualDepartureTime) {
                    console.log(
                        "Driver has already left this stop. Stop is finished.",
                    );
                    return 5;
                } else {
                    console.log("None of the above conditions are met.");
                    return 1;
                }
            } else {
                console.log("No existing entry found");
                // if there is no entry for this stop yet, the step is 1
                return 1;
            }
        },
    },
    persist: {
        pick: [
            "currentDate", // to check whether saved data is up to date
            "routeDataForDate", // saved for the current date being viewed
            "routeExecution", // saved as long as current route was not changed
            "protocols", // saved for all routes
        ],
    },
});

interface routeEntry {
    id: string; // 1, 2, 3A, 3B, ...
    arrivalTime: DateTime;
    waitingTime: number;
    departureTime: DateTime;
    prioritized?: boolean | false;
}

interface route {
    route: routeEntry[];
    duration: number;
    distance_matrix: distanceMatrixEntry[];
}

interface distanceMatrixEntry {
    originId: string;
    destinations: {
        destinationId: string;
        travelTimeMinutes: number;
        destinationAddress: string;
    }[];
}

interface routeData {
    // one row in supabase table "routes"
    id: number; // todo: uuid
    date: string; // format YYYY-MM-DD
    routes: route[];
    deliveries: delivery[];
    comment: string;
}

interface delivery {
    id: string; // 1, 2, 3A, 3B, ...
    order_id: number; // 9841, 9842, ...
    daily_id: string; // 1, 2, 3, ...
    warenkorb_id: string; // "", A, B, C, ...
    type: string; // "delivery" or "pickup"
    address: string;
    hotMeal: boolean;
    earliestTime: DateTime;
    latestTime: DateTime;
    setupTime: number;
    accessible: boolean;
}

interface protocolEntry {
    stop_id: string;
    actualArrivalTime?: DateTime | null;
    actualDeliveryTime?: DateTime | null;
    delay?: number | null;
    delay_reasons?: string | null;
    actualDepartureTime?: DateTime | null;
    prioritized?: boolean | false;
    accessibility?: boolean | false;
}

interface protocol {
    // each route has one protocol in supabase
    // id: string;
    //date: string;
    routeId: number;
    entries: protocolEntry[];
    finished: boolean;
}
